import type { AclEntityMinInfo } from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { ForEach } from "@mdotm/utils/react-extra";
import {
	Controller,
	Dialog,
	DialogFooter,
	type FieldSize,
	Icon,
	ScrollWrapper,
	type StylableProps,
	Text,
} from "@mdotm/shadcn/legacy/components";
import {
	DropdownMenu,
	type DataAttributesProps,
	type DropdownTriggerProps,
	Button,
	SubmitButton,
} from "@mdotm/shadcn/mdotm/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import { type NodeOrFn, overrideClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { unpromisify } from "@mdotm/shadcn/legacy/utils";
import { type ReactNode, useMemo } from "react";
import { type Areas, roleByArea, roleFromAcl } from "./checkers/all";
import { validateACLPermissions } from "./checkers/shared";
import { getRolePermissionByArea } from "./shared";
import { UserSearchAutocomplete } from "./UserSearchAutocomplete";
import { ACLEntryPreview } from "./ACLEntryPreview";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { LinkedEntities } from "./spawn-share-dialog/linked-entities";

/**
 * An ACL entry extended with an optional permission resolution state.
 * Used when the same user appears across multiple entities with potentially
 * different roles (bulk share scenario).
 */
export type ShareACLEntry = Omit<RichAccessControl, "userId" | "permissions"> & {
	/**
	 * Indicates whether this user's permissions are consistent across all shared entities.
	 * - "mixed": the user has different roles on different entities (e.g. Editor on one, Viewer on another)
	 * - undefined: permissions are uniform across all entities
	 *
	 * When the user explicitly picks a new role, this should be cleared to `undefined`.
	 */
	permissionState?: "mixed";
	userId: string;
	permissions: NonNullable<RichAccessControl["permissions"]>;
};

export type ShareDialogProps = MinimumDialogProps & {
	/** Title shown in the dialog header. Fully controlled by the caller.
	 * - Single entity: e.g. `Share "Portfolio ABC"`
	 * - Bulk: e.g. `Share 5 portfolios`
	 */
	header: string;

	/** User ID of the entity owner. Determines who sees the ownership-transfer option. */
	ownerId: string;

	/**
	 * User ID of the currently authenticated user.
	 * Used to:
	 * - Show the user-search input only to the owner
	 * - Warn before downgrading one's own permissions
	 */
	currentUserId: string;

	/**
	 * The merged access-control list to display.
	 * - Single entity: the entity's own ACL
	 * - Bulk: the union of all entities' ACLs; entries with conflicting permissions
	 *   across entities should have `permissionState: "mixed"`
	 */
	acl: ShareACLEntry[];

	/**
	 * Called on submit with the final desired ACL state.
	 * The caller is responsible for persisting the changes (one entity or many).
	 */
	onSubmitAsync(acl: ShareACLEntry[]): MaybePromise<void>;

	/**
	 * Returns the `RichAccessControl` entries matching the current search query. Called during the
	 * autocomplete's render, so it matches against an in-memory list rather than fetching.
	 */
	usersProvider(searchQuery: string): RichAccessControl[];

	/**
	 * Optional content rendered below the ACL list (e.g. a warning about linked entities
	 * that will also be shared). Receives the `aclLinkedEntities` array as argument
	 * when provided as a function.
	 */
	disclaimerProvider?: NodeOrFn<Array<AclEntityMinInfo>>;

	/**
	 * Entities that are implicitly affected by sharing the main entity
	 * (e.g. a portfolio's linked benchmark and universe). Passed to `disclaimerProvider`.
	 */
	aclLinkedEntities: Array<AclEntityMinInfo>;

	/** The permission domain. Controls which permission sets (VIEWER / EDITOR / OWNER) apply. */
	area: Areas;
};

/** Returns the label shown in the permission dropdown trigger for a given ACL entry. */
function getPermissionLabel(entry: ShareACLEntry, area: Areas): string {
	if (entry.permissionState === "mixed") {
		return "Mixed permissions*";
	}
	return validateACLPermissions(entry.userId, [entry], roleByArea[area].EDITOR) ? "Editor" : "Viewer";
}

export function ShareDialog({
	header,
	ownerId: initialOwnerId,
	currentUserId,
	acl: initialAcl,
	show,
	onClose,
	onSubmitAsync,
	usersProvider,
	disclaimerProvider: _disclaimerProvider,
	aclLinkedEntities,
	area,
	onAnimationStateChange,
}: ShareDialogProps): React.JSX.Element {
	// Seed local controller state from props. Re-seeded only when props change.
	const initialState = useMemo(() => ({ ownerId: initialOwnerId, acl: initialAcl }), [initialOwnerId, initialAcl]);
	const someMixedPermission = useMemo(() => initialAcl.some((acl) => acl.permissionState === "mixed"), [initialAcl]);
	const currentUserIsOwner = validateACLPermissions(currentUserId, initialAcl, roleByArea[area].OWNER);

	return (
		<Controller value={initialState}>
			{({ value: { ownerId, acl }, onChange: updateOwnerAndACL }) => (
				<Dialog
					size="large"
					show={show}
					onAnimationStateChange={onAnimationStateChange}
					onClose={onClose}
					onSubmitAsync={async () => {
						await onSubmitAsync(acl);
						onClose?.();
					}}
					header={header}
					footer={
						<DialogFooter
							primaryAction={
								<SubmitButton data-qualifier="ShareDialog/Action(Submit)" size="small">
									Done
								</SubmitButton>
							}
							neutralAction={
								<Button
									size="small"
									palette="tertiary"
									onClick={() => onClose?.()}
									data-qualifier="ShareDialog/Action(Cancel)"
								>
									Cancel
								</Button>
							}
						/>
					}
				>
					{/* Only the owner can add new users */}
					{currentUserId === initialOwnerId && (
						<div className="mb-4">
							<UserSearchAutocomplete
								usersProvider={usersProvider}
								// Exclude users already in the list so they don't appear as search results
								ignoreUserIds={acl.flatMap((entry) => (entry.userId ? [entry.userId] : []))}
								onAdd={(newUsers) =>
									updateOwnerAndACL({
										ownerId,
										acl: acl.concat(
											newUsers.flatMap((user): [ShareACLEntry] | [] =>
												user.userId
													? [
															{
																...user,
																permissionState: undefined,
																userId: user.userId,
																permissions: roleByArea[area].VIEWER,
															},
														]
													: [],
											),
										),
									})
								}
							/>
						</div>
					)}

					<div className="mb-4">
						<Text type="Body/XL/Bold">People with access</Text>
					</div>

					<ScrollWrapper className="max-h-[250px]">
						<div className="space-y-4">
							<ForEach
								collection={[...acl].sort((a, b) => (a.userId === ownerId ? -1 : b.userId === ownerId ? 1 : 0))}
								keyProvider={(entry) => entry.userId ?? "-"}
							>
								{(entry) => (
									<div className="flex items-center gap-2">
										<ACLEntryPreview
											palette={ownerId === entry.userId ? "light" : "dark"}
											isCurrentUser={entry.userId === currentUserId}
											user={entry}
											className="grow"
										/>
										<div>
											{ownerId === entry.userId ? (
												// Owner row: read-only, no dropdown actions
												<DropdownMenu<ShareTriggerExtraProps>
													disabled
													Trigger={ShareTrigger}
													extraTriggerProps={{
														className: "w-[192px]",
														"data-qualifier": `ShareDialog/User(${entry.email})/Permission`,
														children: <span className="inline-block min-w-[60px] text-left">Owner</span>,
													}}
													actions={[]}
												/>
											) : (
												// Non-owner row: permission dropdown
												<DropdownMenu<ShareTriggerExtraProps>
													disabled={
														entry.permissionState === "mixed"
															? false
															: validateACLPermissions(entry.userId, [entry], roleByArea[area].VIEWER) === false
													}
													Trigger={ShareTrigger}
													extraTriggerProps={{
														className: "w-[192px]",
														"data-qualifier": `ShareDialog/User(${entry.email})/Permission`,
														children: (
															<span className="inline-block min-w-[60px] text-left">
																{getPermissionLabel(entry, area)}
															</span>
														),
													}}
													actions={[
														[
															"",
															[
																{
																	onClick: () => {
																		updateOwnerAndACL({
																			ownerId,
																			acl: acl.map((e) =>
																				e.userId === entry.userId
																					? {
																							...e,
																							permissions: getRolePermissionByArea(area, "EDITOR"),
																							// Resolves any "mixed" state — the user has now explicitly chosen
																							permissionState: undefined,
																						}
																					: e,
																			),
																		});
																	},
																	"data-qualifier": `ShareDialog/User(${entry.email})/Permission(Editor)`,
																	icon: "Edit",
																	label: "Editor",
																},
																{
																	onClick: unpromisify(async () => {
																		// Warn the current user before downgrading their own permissions,
																		// since they'll lose the ability to share after saving
																		if (entry.userId === currentUserId) {
																			const confirmed = await spawnYesNoDialog({
																				header: "Update the permission?",
																				children:
																					"This will modify your authorization. You will no longer be able to share.",
																				noButton: "Cancel",
																				yesButton: "Yes, proceed",
																			});
																			if (!confirmed) {
																				return;
																			}
																		}

																		updateOwnerAndACL({
																			ownerId,
																			acl: acl.map((e) =>
																				e.userId === entry.userId
																					? {
																							...e,
																							permissions: getRolePermissionByArea(area, "VIEWER"),
																							permissionState: undefined,
																						}
																					: e,
																			),
																		});
																	}),
																	"data-qualifier": `ShareDialog/User(${entry.email})/Permission(Viewer)`,
																	icon: "show",
																	label: "Viewer",
																},
															],
														],
														[
															"",
															[
																// Transfer ownership — only available if the current user is the owner
																...(currentUserIsOwner
																	? [
																			{
																				onClick: unpromisify(async () => {
																					const newOwner = entry.userId;
																					updateOwnerAndACL({
																						ownerId: newOwner,
																						acl: acl.map((aclEntry) => {
																							//current owner
																							if (aclEntry.userId === newOwner) {
																								return {
																									...aclEntry,
																									permissions: getRolePermissionByArea(area, "OWNER"),
																									permissionState: undefined,
																								};
																							}
																							//previousOwner owner
																							if (ownerId === aclEntry.userId) {
																								return {
																									...aclEntry,
																									permissions: getRolePermissionByArea(area, "EDITOR"),
																									permissionState: undefined,
																								};
																							}

																							return aclEntry;
																						}),
																					});
																				}),
																				"data-qualifier": `ShareDialog/User(${entry.email})/Permission(Transfer ownership)`,
																				label: "Transfer ownership",
																			},
																		]
																	: []),
																{
																	onClick: () => {
																		updateOwnerAndACL({
																			ownerId,
																			acl: acl.filter(
																				(aclEntry) =>
																					aclEntry.userId !== entry.userId &&
																					roleFromAcl(area, entry.userId, acl) !== "OWNER",
																			),
																		});
																	},
																	"data-qualifier": `ShareDialog/User(${entry.email})/Permission(Remove access)`,
																	label: "Remove access",
																},
															],
														],
													]}
												/>
											)}
										</div>
									</div>
								)}
							</ForEach>
						</div>
					</ScrollWrapper>
					{someMixedPermission && (
						<Text type="Body/M/Book" as="p" className="mt-4">
							*Mixed permissions means selected users have different access levels on one or more of the selected items.
						</Text>
					)}
					{aclLinkedEntities && aclLinkedEntities.length > 0 && area === "portfolio" && (
						<>
							<div className="border-b my-4" style={{ borderColor: themeCSSVars.palette_N200 }} />
							<LinkedEntities linkedEntities={aclLinkedEntities} />
						</>
					)}
				</Dialog>
			)}
		</Controller>
	);
}

const selectTriggerClassName = [
	"relative flex flex-row justify-between items-center z-0 rounded-[4px] font-title",
	"overflow-hidden text-ellipsis",

	`border text-[#1d2433] placeholder:text-[var(--palette-N300)] border-[var(--palette-N200)] focus:border-[var(--palette-N500)] focus:outline-hidden`,
	`bg-[#ffffff] disabled:border-[var(--palette-N100)] disabled:text-[var(--palette-N400)] disabled:bg-[var(--palette-N100)]`,
	`data-[invalid=true]:border-[var(--palette-D500)]`,

	`enabled:data-[multi-active=true]:border-[var(--Button-border-secondary)] enabled:data-[multi-active=true]:text-[color:var(--Button-fg-secondary)]`,
].join(" ");

type ShareTriggerExtraProps = {
	children?: ReactNode;
	size?: FieldSize;
} & StylableProps &
	DataAttributesProps;

function ShareTrigger({
	children,
	size = "small",
	className,
	state: _state,
	...forwardProps
}: DropdownTriggerProps & ShareTriggerExtraProps) {
	return (
		<button
			{...forwardProps}
			className={overrideClassName(
				{
					[selectTriggerClassName]: true,
					"text-[16px] h-[38px] px-[16px]": size === "large",
					"text-[14px] h-[34px] px-2.5": size === "small",
					"text-[12px] h-[26px] px-1.5": size === "x-small",
				},
				className,
			)}
			type="button"
		>
			{children}&nbsp;
			<span className="transition-transform data-[state=open]:transform-[rotateX(180deg)] flex">
				<Icon icon="Down" />
			</span>
		</button>
	);
}
